import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const TAGS = ["Tea Weekly", "Ambassador", "Applicant", "Ceremony RSVP", "Light Ceremony", "Waterfall Medicine", "Healers Clinic", "Healing Womb", "Cosmic Business Blueprint", "Home Care Apprenticeship", "Healing Ambassadors Interest"];
const STATUS = { retry: "Retrying soon", failed: "Needs a retry", waiting_for_key: "Waiting for Mailchimp key" };

export const AdminMailchimp = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/mailchimp").then((r) => setData(r.data))
      .catch(() => setData({ connected: false, reason: "The Mailchimp status could not be loaded just now", added_this_week: {}, pending: [] }));
  }, []);
  const retry = async (id) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/admin/mailchimp/retry", null, { params: id ? { job_id: id } : {} });
      setData(d);
      toast.success(id ? "Retry finished." : "Retrying everyone now. Refresh in a minute to see the results.");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  if (!data) return null;
  return (
    <div className="card-soft p-8 space-y-8" data-testid="admin-mailchimp">
      <div className="flex items-center gap-3">
        <span className={`h-3 w-3 rounded-full ${data.connected ? "bg-[#577731]" : "bg-[#b6689a]"}`} />
        <p className="font-serif-h text-2xl text-[#232b1b]" data-testid="mailchimp-status">
          {data.connected ? `Connected to "${data.audience_name}"` : "Not connected"}
        </p>
      </div>
      <p className="text-sm text-[#232b1b]/70">
        {data.connected
          ? `Audience ID ${data.audience_id} · ${data.member_count ?? 0} contacts in Mailchimp. New people are added automatically and tagged by the form they used.`
          : `${data.reason}. Every form keeps working and saving here; people will be added once Mailchimp is connected.`}
      </p>
      <div>
        <p className="eyebrow">Added this week</p>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="mailchimp-week-counts">
          {TAGS.map((t) => (
            <div key={t} className="rounded-2xl bg-[#b1cc90]/20 px-4 py-3">
              <p className="text-xs text-[#232b1b]/60">{t}</p>
              <p className="font-serif-h text-2xl text-[#577731]">{data.added_this_week[t] || 0}</p>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between gap-4">
          <p className="eyebrow">Not added yet</p>
          {data.pending.length > 0 && (
            <button disabled={busy} onClick={() => retry()} className="btn btn-olive !py-2 !px-5 text-xs" data-testid="mailchimp-retry-all-btn">Retry All</button>
          )}
        </div>
        {data.pending.length === 0 ? (
          <p className="mt-3 text-sm text-[#232b1b]/60" data-testid="mailchimp-no-pending">Everyone has been added. Nothing is waiting.</p>
        ) : (
          <ul className="mt-3 divide-y divide-[#dacac1]/70 text-sm" data-testid="mailchimp-pending-list">
            {data.pending.map((j) => (
              <li key={j.id} className="py-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p>{j.email} <span className="text-[#232b1b]/50">· {j.tags.join(", ")}</span></p>
                  <p className="text-xs text-[#b6689a]">{STATUS[j.status]}{j.last_error ? ` · ${j.last_error.slice(0, 120)}` : ""}</p>
                </div>
                <button disabled={busy} onClick={() => retry(j.id)} className="btn btn-ghost !py-1.5 !px-4 text-xs" data-testid={`mailchimp-retry-${j.id}`}>Retry</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
